import { responseError, WhopApiError, whopApiFetch } from "../lib/http";
import type { Identity } from "./types";

/**
 * Whop API boundary for authentication.
 *
 * Validates a key by calling the requesting-account endpoint.
 */

const DEFAULT_BASE_URL = "https://api.whop.com/api/v1";

export function apiBaseUrl(env: NodeJS.ProcessEnv = process.env): string {
	return env.WHOP_API_BASE_URL?.trim() || DEFAULT_BASE_URL;
}

/**
 * Base URL for the OAuth endpoints (`/oauth/authorize`, `/oauth/token`,
 * `/oauth/userinfo`), which live at the API host root rather than under
 * `/api/v1`. Tolerates a base URL with or without the `/api/v1` suffix and a
 * trailing slash.
 */
export function oauthBaseUrl(env: NodeJS.ProcessEnv = process.env): string {
	return apiBaseUrl(env)
		.replace(/\/+$/, "")
		.replace(/\/api\/v1$/, "");
}

export type ValidateResult =
	| { status: "valid"; identity: Identity }
	| { status: "underscoped"; identity: Identity }
	| { status: "invalid" }
	| { status: "error"; message: string };

export async function validateApiKey(
	apiKey: string,
	baseUrl: string = apiBaseUrl(),
): Promise<ValidateResult> {
	try {
		const response = await whopApiFetch(
			`${baseUrl.replace(/\/$/, "")}/accounts/me`,
			apiKey,
		);
		if (!response.ok) throw await responseError(response);
		const account = (await response.json()) as {
			id: string;
			title: string;
			route: string;
		};
		return {
			status: "valid",
			identity: { id: account.id, title: account.title, route: account.route },
		};
	} catch (error) {
		if (error instanceof WhopApiError && error.status === 401)
			return { status: "invalid" };
		if (error instanceof WhopApiError && error.status === 403)
			return { status: "underscoped", identity: {} };
		return {
			status: "error",
			message: error instanceof Error ? error.message : String(error),
		};
	}
}
